/**
 * Background-upload store — "upload em segundo plano" (evaluations/new.tsx).
 *
 * WHY THIS EXISTS: submitting an interview with a video used to hold the
 * person on the create-evaluation screen for however long the video took to
 * PUT to R2 — minutes, for a real interview recording. The owner-approved fix
 * releases the screen the instant the (still-synchronous) format/size guards
 * pass, and runs video-upload → transcript-upload → createEvaluation HERE
 * instead, in a Zustand module singleton — the SAME survives-navigation
 * pattern `useAssessmentEditorStore` already uses in this codebase (a plain
 * `create()` store is a module-level object, never unmounted by React Router,
 * so no Context/Provider had to be added to `App.tsx`).
 *
 * ONLY THE VIDEO PATH COMES HERE. A transcript-only interview (paste or a
 * small text/PDF/DOCX attachment) stays on the ORIGINAL synchronous
 * `useMutation` in `new.tsx` — it is fast enough that releasing the screen
 * early would add complexity (a second notification surface) for no
 * perceptible benefit. See the decision note in `new.tsx`'s `onSubmit`.
 *
 * CONCURRENCY: uploads are tracked as an ARRAY, not a single slot — starting a
 * second background upload does not block or replace the first. Each upload
 * targets its own presigned R2 URL and its own `createEvaluation` call, so
 * there is no shared resource a single-flight lock would be protecting; the
 * only cost of allowing N at once is more entries in the indicator list,
 * which is exactly what the indicator is for.
 *
 * CANCELLATION: only the two upload steps accept an `AbortSignal` (mirroring
 * `uploadFile`/`putToPresignedUrl`) — `createEvaluation` does not, so once a
 * task reaches the `creating` phase there is nothing left worth aborting
 * (the uploads are already durably in R2). `cancelTask` before that point
 * aborts the in-flight XHR for real, via the SAME AbortController threaded
 * into `uploadFile`.
 *
 * TAB CLOSE: this store holds no persistence (no localStorage/IndexedDB) —
 * closing or reloading the tab loses whatever is in flight, same as before
 * this feature existed. That is an explicit, owner-accepted limit (creating
 * the evaluation before the upload finishes would need new backend state,
 * out of scope). The `beforeunload` warning that tells the person about this
 * lives in `BackgroundUploadIndicator`, which reads `tasks` from this store.
 */
import { create } from 'zustand'
import { toast } from 'sonner'
import { createEvaluation, uploadFile } from '@/lib/api/evaluations'
import { queryClient } from '@/lib/query-client'
import { messageOf } from '@/lib/utils'

export type BackgroundUploadPhase =
  | 'uploading-video'
  | 'uploading-transcript'
  | 'creating'
  | 'done'
  | 'error'

/** One in-flight (or finished) background upload, as shown by the indicator. */
export interface BackgroundUploadTask {
  id: string
  applicationId: string
  /** The video's file name — the only thing worth naming in the indicator. */
  fileName: string
  /** Video-upload fraction 0..1. Frozen once the video step completes. */
  progress: number
  phase: BackgroundUploadPhase
  /** Set only when `phase === 'error'` — the SAME "which step failed" message
   *  the synchronous flow already showed inline (never a generic fallback). */
  error: string | null
  /** Set only when `phase === 'done'` — where "Ver avaliação" navigates to. */
  evaluationId: string | null
}

export interface StartInterviewUploadInput {
  applicationId: string
  /** Shown in the indicator; also the value `createEvaluation` never needs
   *  directly (that reads r2Key, not the original name). */
  fileName: string
  videoFile: File
  videoContentType: string
  /** Already resolved by the caller (paste / attached file / extracted PDF-
   *  DOCX text) — this store does not know about transcript MODES, only the
   *  final File + content type ready to upload. `null` = no transcript. */
  transcriptFile: File | null
  transcriptContentType: string | null
}

interface BackgroundUploadState {
  tasks: BackgroundUploadTask[]
  /** Starts the video→transcript→create flow in the background and returns
   *  the new task id immediately — does NOT wait for any network call. */
  startInterviewUpload: (input: StartInterviewUploadInput) => string
  /** Aborts the in-flight upload for `id`, if any, and drops the task with no
   *  error surfaced (an intentional cancel is not a failure). */
  cancelTask: (id: string) => void
  /** Removes a finished (`done`/`error`) task from the visible list. */
  dismissTask: (id: string) => void
}

/** AbortControllers live OUTSIDE the store state — they are not serialisable
 *  and no UI ever needs to react to their identity, only to call `.abort()`
 *  on the one matching a task id. */
const controllers = new Map<string, AbortController>()

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError'
}

/**
 * Runs `fn`, and on failure re-throws an `AbortError` AS-IS (so the outer
 * handler can tell "cancelled" from "actually failed") or wraps anything else
 * with `label` — reproducing the "each step names itself" contract from the
 * synchronous flow, now across an async boundary that has no component to
 * show it inline.
 */
async function namedStep<T>(fn: () => Promise<T>, label: string): Promise<T> {
  try {
    return await fn()
  } catch (err) {
    if (isAbortError(err)) throw err
    throw new Error(`${label}: ${messageOf(err)}`)
  }
}

export const useBackgroundUploadStore = create<BackgroundUploadState>((set, get) => ({
  tasks: [],

  startInterviewUpload: (input) => {
    const id = crypto.randomUUID()
    const task: BackgroundUploadTask = {
      id,
      applicationId: input.applicationId,
      fileName: input.fileName,
      progress: 0,
      phase: 'uploading-video',
      error: null,
      evaluationId: null,
    }
    set((state) => ({ tasks: [...state.tasks, task] }))

    void runInterviewUpload(id, input, set, get)
    return id
  },

  cancelTask: (id) => {
    controllers.get(id)?.abort()
  },

  dismissTask: (id) => {
    set((state) => ({ tasks: state.tasks.filter((t) => t.id !== id) }))
  },
}))

/** Immutable per-task patch — never mutates the array or a task in place. */
function patchTask(
  set: (fn: (state: BackgroundUploadState) => Partial<BackgroundUploadState>) => void,
  id: string,
  patch: Partial<BackgroundUploadTask>,
): void {
  set((state) => ({
    tasks: state.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
  }))
}

async function runInterviewUpload(
  id: string,
  input: StartInterviewUploadInput,
  set: (fn: (state: BackgroundUploadState) => Partial<BackgroundUploadState>) => void,
  get: () => BackgroundUploadState,
): Promise<void> {
  const controller = new AbortController()
  controllers.set(id, controller)

  try {
    const videoR2Key = await namedStep(
      () =>
        uploadFile(input.videoFile, 'video', {
          contentType: input.videoContentType,
          onProgress: (fraction) => patchTask(set, id, { progress: fraction }),
          signal: controller.signal,
        }),
      'Falha ao enviar o vídeo',
    )

    let userTranscriptR2Key: string | undefined
    if (input.transcriptFile) {
      patchTask(set, id, { phase: 'uploading-transcript' })
      userTranscriptR2Key = await namedStep(
        () =>
          uploadFile(input.transcriptFile as File, 'user-transcript', {
            contentType: input.transcriptContentType ?? (input.transcriptFile as File).type,
            signal: controller.signal,
          }),
        'Falha ao enviar a transcrição',
      )
    }

    patchTask(set, id, { phase: 'creating' })
    const evaluation = await namedStep(
      () =>
        createEvaluation({
          applicationId: input.applicationId,
          type: 'interview',
          videoR2Key,
          ...(userTranscriptR2Key && { userTranscriptR2Key }),
        }),
      'Falha ao criar a avaliação',
    )

    patchTask(set, id, { phase: 'done', evaluationId: evaluation.id })
    toast.success('Avaliação criada', {
      description: `O vídeo "${input.fileName}" terminou de ser enviado em segundo plano.`,
    })
    void queryClient.invalidateQueries({ queryKey: ['applications'] })
    void queryClient.invalidateQueries({
      queryKey: ['evaluations', 'by-application', input.applicationId],
    })
  } catch (err) {
    if (isAbortError(err)) {
      // Intentional cancel (cancelTask) — not a failure, nothing to report.
      get().dismissTask(id)
      return
    }
    const message = messageOf(err)
    patchTask(set, id, { phase: 'error', error: message })
    toast.error(message)
  } finally {
    controllers.delete(id)
  }
}
